import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SectionCard } from './SectionCard'
import { Skeleton } from '@/components/ui/skeleton'
import { usePendingTasks } from '@/hooks/useDashboard'
import { formatDate, formatDateTime } from '@/lib/format'
import { cn } from '@/lib/utils'
import { TASK_PRIORITY_LABELS, relatedTypeLabel } from '@/types/tasks'
import type { Task, TaskPriority } from '@/types'
import {
  DashboardListArrowIcon,
  DashboardListDotIcon,
  PendingTasksHeaderIcon,
  TaskItemIcon,
} from '@/components/icons/DashboardListIcons'

function isOverdue(task: Task): boolean {
  if (!task.due_date || task.status === 'done') return false
  const due = new Date(task.due_date)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return due.getTime() < today.getTime()
}

function isDueToday(dueDate: string | null): boolean {
  if (!dueDate) return false
  const due = new Date(dueDate)
  const today = new Date()
  return due.toDateString() === today.toDateString()
}

function formatTaskDueDate(value: string, todayLabel: string): string {
  const hasTime = /(?:T|\s)\d{2}:\d{2}/.test(value)
  if (isDueToday(value)) {
    if (!hasTime) return todayLabel
    const date = new Date(value)
    const time = date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })
    return `${todayLabel} ${time}`
  }
  return hasTime ? formatDateTime(value) : formatDate(value)
}

function TaskPriorityTag({ priority }: { priority: TaskPriority }) {
  const styles: Record<TaskPriority, string> = {
    low: 'bg-[#f4f5f8] text-[#171a20]',
    medium: 'bg-[#ebf1fe] text-[#1d44b3]',
    high: 'bg-[#ffe7ba] text-[#ad4e00]',
    urgent: 'bg-[#fecaca] text-[#7f1d1d]',
  }
  return (
    <span
      className={cn(
        'inline-flex h-6 shrink-0 items-center rounded-lg px-2 font-sans text-xs font-medium leading-4',
        styles[priority]
      )}
    >
      {TASK_PRIORITY_LABELS[priority]}
    </span>
  )
}

export function PendingTasksPanel() {
  const { t } = useTranslation('dashboard')
  const navigate = useNavigate()
  const { data, isLoading, isError } = usePendingTasks(3)
  const tasks = data?.data ?? []

  let body: React.ReactNode
  if (isLoading) {
    body = (
      <div className='flex flex-col gap-3'>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className='h-17.5 w-full rounded-xl' />
        ))}
      </div>
    )
  } else if (isError) {
    body = (
      <p className='py-8 text-center text-sm text-destructive'>
        {t('pendingTasks.unableToLoad')}
      </p>
    )
  } else if (tasks.length === 0) {
    body = (
      <p className='py-8 text-center text-sm text-muted-foreground'>
        {t('pendingTasks.empty')}
      </p>
    )
  } else {
    body = (
      <ul className='flex flex-col gap-3'>
        {tasks.map(task => {
          const overdue = isOverdue(task)
          const dueToday = isDueToday(task.due_date)
          const dueUrgent = overdue || dueToday
          const related = relatedTypeLabel(task.related_type)

          return (
            <li key={task.id}>
              <button
                type='button'
                onClick={() => navigate(`/tasks/${task.id}`)}
                className='flex h-17.5 w-full items-start gap-3 overflow-hidden rounded-xl border border-[#e7e9ef] bg-white p-3 text-left transition-colors hover:bg-[#f4f5f8]/50'
              >
                <span className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#f4f5f8]'>
                  <TaskItemIcon />
                </span>
                <span className='flex min-w-0 flex-1 flex-col gap-1'>
                  <span className='block truncate font-sans text-sm font-medium leading-[1.4] text-[#171a20]'>
                    {task.title}
                  </span>
                  <span className='flex min-w-0 items-center gap-2 font-sans text-xs font-normal leading-[1.4]'>
                    {task.due_date && (
                      <span
                        className={cn(
                          'truncate',
                          dueUrgent ? 'text-[#b91c1c]' : 'text-[#3d434e]'
                        )}
                      >
                        {t('pendingTasks.due', {
                          date: formatTaskDueDate(
                            task.due_date,
                            t('pendingTasks.today')
                          ),
                        })}
                      </span>
                    )}
                    {related && (
                      <>
                        <DashboardListDotIcon className='size-1 shrink-0' />
                        <span className='truncate text-[#3d434e]'>
                          {related}
                        </span>
                      </>
                    )}
                  </span>
                </span>
                <TaskPriorityTag priority={task.priority} />
              </button>
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <SectionCard
      variant='chart'
      title={t('pendingTasks.title')}
      className='h-86.5'
      headerClassName='min-h-16'
      contentClassName='h-70.5'
      headerIcon={<PendingTasksHeaderIcon />}
      headerIconClassName='bg-[#fbf3d9]'
      headerRight={
        <Link
          to='/tasks'
          className='inline-flex items-center gap-2 rounded-lg p-2 font-sans text-xs font-medium leading-none text-[#ff5a1f] hover:underline'
        >
          {t('pendingTasks.viewAll')}
          <DashboardListArrowIcon />
        </Link>
      }
    >
      {body}
    </SectionCard>
  )
}

export default PendingTasksPanel
